import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Icon,
  Loader,
  TabContent,
  TabGroup,
  TabItem,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  getSidePanelHistory,
  getSidePanelProgress,
  getSidePanelSummary,
  setIsSidePanelLoading,
  setSidePanelMetaData,
} from '../../../../redux/certlist/action';
import { FORCED_CANCELLATION_MESSAGE } from '../../../../redux/certlist/types';
import {
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../../utils/constant';
import { getMessageKey } from '../../../../utils/utils';
import {
  LIST_SP_TAB_HISTORY,
  LIST_SP_TAB_PROGRESS,
  LIST_SP_TAB_SUMMARY,
} from '../constant';

import CertsListSidePanelBodyContent from './CertsListSidePanelBodyContent';
import {
  LIST_SP_AVAILABLE_TABS,
  PROMISE_FULFILLED,
  PROMISE_REJECTED,
} from './constant';

import './CertsListSidePanelBody.css';

function CertsListSidePanelBody({ handleCloseSidePanel, tabGroupRef }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {
    global: { mediaQueryValue },
    sidePanel: { isSidePanelLoading, connectedTileId, sidePanelMetaData },
  } = useSelector((state) => state?.certifications?.list);
  const { messages } = useSelector((state) => state?.user);

  const [activeTab, setActiveTab] = useState(LIST_SP_TAB_SUMMARY);
  const [sidePanelData, setSidePanelData] = useState({
    networkFailedTabs: [],
    sidePanelMetaData,
  });

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;
  const isProgressTabEnabled = Boolean(
    Number(sidePanelData?.sidePanelMetaData.newCertUX) === 1
  );

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const callApiForSidePanel = (certkey) => {
    if (!isSidePanelLoading) {
      dispatch(setIsSidePanelLoading(true));
    }

    setSidePanelData((prev) => {
      const {
        [LIST_SP_TAB_SUMMARY]: removedSummary,
        [LIST_SP_TAB_HISTORY]: removedHistory,
        [LIST_SP_TAB_PROGRESS]: removedProgress,
        ...rest
      } = prev;

      return {
        ...rest,
        networkFailedTabs: [],
      };
    });

    const promiseForApiCalls = [];

    promiseForApiCalls.push(dispatch(getSidePanelSummary(certkey)));
    promiseForApiCalls.push(dispatch(getSidePanelHistory(certkey)));
    // TODO: Need to remove this logic when API is reeady to support other campaigns
    promiseForApiCalls.push(
      dispatch(getSidePanelProgress(certkey, isProgressTabEnabled))
    );

    Promise.allSettled(promiseForApiCalls)
      .then((responses) => {
        const isApiSetFailed = responses?.every(
          (response) => response.status === PROMISE_REJECTED
        );

        if (isApiSetFailed) throw responses;

        responses.forEach((response) => {
          if (response.status === PROMISE_FULFILLED) {
            setSidePanelData((prev) => ({
              ...prev,
              [response.value.tab]: response.value.data,
            }));

            // Updating progress for side panel
            const isProgressPercentage = Boolean(
              response.value.tab === LIST_SP_TAB_SUMMARY &&
                response.value.data?.progress
            );

            if (isProgressPercentage) {
              const { progress } = response.value.data;

              const updatedSidePanelMetaData = {
                ...sidePanelMetaData,
                progress: `${progress}%`,
              };

              dispatch(setSidePanelMetaData(updatedSidePanelMetaData));
            }
          }

          if (response.status === PROMISE_REJECTED) {
            const errorObject = JSON.parse(response.reason.message);

            if (errorObject.error?.message !== FORCED_CANCELLATION_MESSAGE) {
              setSidePanelData((prev) => ({
                ...prev,
                networkFailedTabs: [...prev.networkFailedTabs, errorObject.tab],
              }));
            }
          }
        });

        dispatch(setIsSidePanelLoading(false));
      })
      .catch((err) => {
        const errors = err.map((error) => JSON.parse(error.reason.message));
        const isForcedCancellation = errors.every(
          (error) => error.error.message === FORCED_CANCELLATION_MESSAGE
        );

        if (!isForcedCancellation) {
          setSidePanelData((prev) => ({
            ...prev,
            networkFailedTabs: [...LIST_SP_AVAILABLE_TABS],
          }));

          dispatch(setIsSidePanelLoading(false));
        }
      });
  };

  const retriggerApisForError = () => callApiForSidePanel(connectedTileId);

  useEffect(() => {
    if (connectedTileId) {
      setSidePanelData((prev) => ({
        ...prev,
        sidePanelMetaData,
      }));

      callApiForSidePanel(connectedTileId);

      setActiveTab(LIST_SP_TAB_SUMMARY);
    }
  }, [connectedTileId]);

  const renderBodyContent = () => {
    if (isSidePanelLoading) {
      return (
        <Loader
          kind='dots'
          format='inline'
          color='brand'
          className='CertsListSidePanelBody-loader'
        />
      );
    }

    return (
      <>
        <TabGroup
          moreContainerRef={tabGroupRef}
          value={activeTab}
          isVerticalWithOverflow={isMobileXlargeWidth}
          onChange={handleTabChange}
          isTabOrderBeFixed>
          <TabItem
            label={
              messages.CERTS_SUMMARY ||
              localize(intl, getMessageKey('CERTS_SUMMARY'))
            }
            value={LIST_SP_TAB_SUMMARY}
            icon={<Icon kind='Document' color='neutral-600' />}
            size='medium'
          />
          <TabItem
            label={
              messages.CERTS_HISTORY ||
              localize(intl, getMessageKey('CERTS_HISTORY'))
            }
            value={LIST_SP_TAB_HISTORY}
            icon={<Icon kind='RequestHistory' color='neutral-600' />}
            size='medium'
          />
          {isProgressTabEnabled && (
            <TabItem
              label={
                messages.CERTS_PROGRESS ||
                localize(intl, getMessageKey('CERTS_PROGRESS'))
              }
              value={LIST_SP_TAB_PROGRESS}
              icon={<Icon kind='Workflow' color='neutral-600' />}
              size='medium'
            />
          )}
        </TabGroup>
        <TabContent value={activeTab} tabValue={LIST_SP_TAB_SUMMARY}>
          <CertsListSidePanelBodyContent
            tab={LIST_SP_TAB_SUMMARY}
            sidePanelData={sidePanelData}
            retriggerApisForError={retriggerApisForError}
            handleCloseSidePanel={handleCloseSidePanel}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </TabContent>
        <TabContent value={activeTab} tabValue={LIST_SP_TAB_HISTORY}>
          <CertsListSidePanelBodyContent
            tab={LIST_SP_TAB_HISTORY}
            sidePanelData={sidePanelData}
            retriggerApisForError={retriggerApisForError}
            handleCloseSidePanel={handleCloseSidePanel}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </TabContent>
        <TabContent value={activeTab} tabValue={LIST_SP_TAB_PROGRESS}>
          <CertsListSidePanelBodyContent
            tab={LIST_SP_TAB_PROGRESS}
            sidePanelData={sidePanelData}
            retriggerApisForError={retriggerApisForError}
            handleCloseSidePanel={handleCloseSidePanel}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </TabContent>
      </>
    );
  };

  return (
    <Box tag='div' className='CertsListSidePanelBody-wrapper'>
      {renderBodyContent()}
    </Box>
  );
}

CertsListSidePanelBody.propTypes = {
  handleCloseSidePanel: PropTypes.func,
  tabGroupRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
};

CertsListSidePanelBody.defaultProps = {
  handleCloseSidePanel: () => {},
};

export default React.memo(CertsListSidePanelBody);
